import { useCallback, useMemo, useState } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import FJDropdownNew from '../../../common/components/FJDropDown/FJDropdownNew.jsx';
import FJLocalStore from '../../../store/FJLocalStore.js';
import ChooseIcon from '../../../assets/svg/correct.svg?react';
import CloseIcon from '../../../assets/svg/cancel-2.svg?react';
import ArrowIcon from '../../../assets/svg/down-arrow.svg?react';

import styles from './FJStylePicker.module.less';

const FJStylePicker = ({ onChange, selectedOptions, options, btnText, title, className }) => {
    const [active, setActive] = useState(false);
    const handleChange = useCallback(
        (value, checked) => {
            if (checked) {
                onChange?.([...selectedOptions, value]);
            } else {
                onChange?.(selectedOptions?.filter(option => option !== value) ?? []);
            }
        },
        [selectedOptions],
    );

    const handleClear = useCallback(
        e => {
            e.stopPropagation();
            onChange?.([]);
        },
        [onChange],
    );

    const localOptions = useMemo(() =>
        options.map(({ value, label }) => ({
            value: value,
            label: label,
        })),
    );

    const _renderPresetTag = option => {
        return (
            <label
                key={option.value}
                className={styles.preset_tag}
                data-checked={selectedOptions?.includes(option.value)}
            >
                <input
                    type='checkbox'
                    checked={selectedOptions?.includes(option.value)}
                    onChange={e => handleChange(option.value, e.target.checked)}
                    className={styles.style_checkbox}
                />
                {FJLocalStore._(option.label)}
                <div className={styles.fake_div}></div>
                <ChooseIcon className={classNames(styles.checked_icon, styles.icon)} />
                <CloseIcon fill='var(--font-color-primary)' className={classNames(styles.close_icon, styles.icon)} />
            </label>
        );
    };

    const _renderTrigger = config =>
        !!selectedOptions?.length ? (
            <div
                className={styles.trigger}
                data-open={active}
                onClick={() => {
                    config.onClick();
                    setActive(prev => !prev);
                }}
                ref={config.ref}
            >
                {selectedOptions?.length} {FJLocalStore._('selected')}
                <CloseIcon className={styles.close_icon} onClick={handleClear} />
                <ArrowIcon className={styles.arrow_icon} />
            </div>
        ) : (
            <div
                className={styles.trigger}
                data-open={active}
                onClick={() => {
                    config.onClick();
                    setActive(prev => !prev);
                }}
                ref={config.ref}
            >
                {btnText ?? FJLocalStore._('add-style')}
                <ArrowIcon className={styles.arrow_icon} />
            </div>
        );

    const _renderOverlay = config => (
        <div className={styles.dropdown_content} ref={config.ref}>
            <p className={styles.dropdown_title}>{title ?? FJLocalStore._('select-your-favorite-styles')}</p>
            {localOptions?.map(option => _renderPresetTag(option))}
        </div>
    );

    return (
        <div className={className}>
            <FJDropdownNew
                active={active}
                isTopLayer={true}
                offsetLeft={36}
                offsetTop={-145}
                placement='rightTop'
                trigger='click'
                renderOverlay={_renderOverlay}
                renderTrigger={_renderTrigger}
            />
        </div>
    );
};

FJStylePicker.propTypes = {
    onChange: PropTypes.func,
    selectedOptions: PropTypes.arrayOf(PropTypes.string),
    options: PropTypes.arrayOf(
        PropTypes.shape({
            label: PropTypes.string,
            value: PropTypes.string,
        }),
    ),
    btnText: PropTypes.string,
    title: PropTypes.string || PropTypes.element,
    className: PropTypes.string,
};

export default FJStylePicker;
